import { Authenticator, UserInfo, UserType } from '@amzn/awscat-portal-authentication-library';
import moment from 'moment';

import { constants } from './Localization';
import { ONE_HUNDRED } from './uomConversion';

interface UserProfile {
    type: string;
}
export const isAWSUser = (auth: Authenticator): boolean => auth.isAuthenticated() && auth.getUserInfo().organizationId === 'AWS';

export const isCognito = async (auth: Authenticator): Promise<boolean> => {
    try {
        const result = await auth.getUserInfoList([auth.getUserInfo().userId]) as Array<UserInfo & UserProfile>;
        return result[0].type !== UserType.AWS && result[0].type !== UserType.PARTNER;
    } catch (err) {}
    return false;
};

export const CATDEV_PROVIDER = 'catdev';
export const CATDEV_NAME = 'CATDEV';
export const CAT_PORTAL_PROVIDER = 'catportal';
export const CAT_PORTAL_PREFIX = 'catportal-';
export const getAuthProvider = () => {
    const xAuthProvider = window.sessionStorage.getItem('x-auth-provider');
    if (!xAuthProvider) {
        return CAT_PORTAL_PROVIDER;
    }
    return JSON.parse(xAuthProvider);
};

/**
 * Returns the number of decimal places in the given number, 0 if none
 * @param value a given number
 * @returns 
 */
export const numberOfDecimals = (value: number): number => {
    return value.toString().split('.')[1]?.length ?? 0;
};

/**
 * Format js strings with substitutions
 * @param source template string, e.g. {0} is a {1}
 * @param params replacements, e.g. ['Apple', 'fruit']
 */
export const format = (source: string, params: (string | number | undefined)[]): string => {
    params.forEach((param: string | number | undefined, index: number) => {
        source = source.replace(new RegExp('\\{' + index + '\\}', 'g'), `${param}`);
    });
    return source;
};
/**
 * Returns appId used for Feedback Service
 * @returns string appId
 */
export const getAppId = (): string => {
    let appId = 'mpa';
    if(window.location.hostname.includes('local')) {
        appId = 'mpa-localdev';
    }
    return appId;
};

/**
 * Generate plural string from singular string
 * @param count count number
 * @param singular singular format
 * @param plural used for irregular words, by default an 's' is attached to singular string
 * @returns plural string
 */
export const generatePluralStr = (count: number, singular: string, plural?: string, showCount?: boolean): string => {
    const pluralStr = count <= 1 ? singular : (plural || `${singular}s` || '');
    return showCount ? `${count} ${pluralStr}` : pluralStr;
};

/**
 * Distribute data into multiple bins
 * For example, data = [0.12, 0.35, 0.7], min = 0, max = 1, numInterval = 2,
 * return two bins:
 * [{
 *      lowerLimit: 0,
 *      upperLimit: 0.5,
 *      count: 2,
 * }, {
 *      lowerLimit: 0.5,
 *      upperLimit: 1,
 *      count: 1,
 * }]
 * @param data 
 * @param minValue 
 * @param maxValue 
 * @param numIntervals 
 */
export const generateDistributions = (data: number[], minValue: number, maxValue: number, numIntervals: number): Distribution[] => {
    if (data.length === 0 || numIntervals <= 0 || minValue > maxValue) {
        return [];
    }
    const interval = (maxValue - minValue) / numIntervals;
    const bins: {[key: number]: Distribution} = {};
    for (let intervalIndex = 0; intervalIndex < numIntervals; intervalIndex++) {
        const lowerLimit = minValue + interval * intervalIndex;
        bins[intervalIndex] = {
            count: 0,
            lowerLimit,
            upperLimit: lowerLimit + interval,
        };
    }
    data.forEach(num => {
        const quotient = Math.floor((num - minValue) / interval);
        if (bins[quotient]) {
            bins[quotient].count ++;
        }
    });
    return Object.values(bins);
};

interface Distribution {
    lowerLimit: number;
    upperLimit: number;
    count: number;
}

/**
 * Common configs for Plotly graph
 */
export const commonGraphConfig = {
    displaylogo: false,
    displayModeBar: false,
    resposive: true
};

export const deepCopy = (target: any): any => {
    return JSON.parse(JSON.stringify(target));
};

export const roundToHundredth = (value: number): number => {
    return Math.round(value * ONE_HUNDRED) / ONE_HUNDRED;
};

/**
 * Round a number to an integer, returns zero if null or undefined
 * @param value 
 * @returns 
 */
export const roundToInteger = (value: number | null | undefined): number => {
    return value ? Math.round(value) : 0;
};

export const formatRecommendationPaymentOption = (payment: string): string => {
    if (payment === 'ON_DEMAND') {
        return constants.ON_DEMAND;
    }
    // payment option could only be 1 year or 3 years
    let year = 1;
    let type = `${constants.ALL} ${constants.UPFRONT}`;
    if (payment.includes('THREE')) {
        year = 3;
    } else if (payment.includes('ONE')) {
        year = 1;
    }
    if (payment.includes('NO')) {
        type = `${constants.NO} ${constants.UPFRONT}`;
    } else if (payment.includes('PARTIAL')) {
        type = `${constants.PARTIAL} ${constants.UPFRONT}`;
    }
    return `${constants.STANDARD} ${year}${constants.YR} - ${type}`;
};

/**
 * Returns formatted date string to append to filename
 * @param date your own date to format
 * @returns The provided date or current, formatted as ISO (YYYY-MM-DDTHH:mm:ss.sssZ)
 */
export function getFormattedDateAndTime(date?: Date): string {
    const formattedDate = (date || new Date()).toISOString();
    return formattedDate;
}

/**
 * Compare two items and return a number indicating sort order
 * 0 if equal, -1 if a is less than b, and 1 if a is greater than b
 * @param a 
 * @param b 
 * @returns 
 */
export function compare<T>(a: T, b: T): number {
    if (a === b) {
        return 0;
    } else if (a < b) {
        return -1;
    } else if (a > b) {
        return 1;
    } else {
        return 0;
    }
};
/**
 * Converts value to string, for filtering and comparison
 * @param  { string | number | T[keyofT] } value
 * @returns string
 */
export function convertToString<T>(value: string | number | T[keyof T]): string {
    if (typeof value === 'string') {
        return value;
    }
    else if (typeof value === 'number') {
        return value.toString();
    } else {
        return JSON.stringify(value);
    }
}

/**
 * Convert a string date to a Date type, default format is the polaris date picker format
 * using moment which will create the Date in the local timezone
 * not using new Date('YYYY-MM-DD') because it creates the date in UTC timezone 
 * @param dateStr 
 * @param format 
 * @returns 
 */
export const convertDateStringToDate = (dateStr: string, format = 'YYYY-MM-DD'): Date => {
    const momentDate = moment(dateStr, format);
    const date: Date = momentDate.toDate();
    return date;
};
